import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Shield, CheckCircle, Clock, AlertCircle, ExternalLink, 
  FileText, Lock, Eye, Server, Database, Key, Users,
  RefreshCw, Download, Settings, ChevronRight, AlertTriangle,
  PieChart, Zap
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const TrustCenterPage = () => {
  const [loading, setLoading] = useState(true);
  const [trustData, setTrustData] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [scoringLoading, setScoreinLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('security');

  const fetchTrustCenter = useCallback(async () => {
    try {
      setLoading(true);
      const [trustRes, scoreRes] = await Promise.all([
        api.get('/trust/security-and-compliance'),
        api.get('/compliance/score')
      ]);
      setTrustData(trustRes.data);
      setScoreData(scoreRes.data);
    } catch (error) {
      console.error('Error fetching trust center:', error);
      toast.error('Failed to load trust center');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrustCenter();
  }, [fetchTrustCenter]);

  const handleRecalculateScore = async () => {
    try {
      setScoreinLoading(true);
      toast.info('Recalculating compliance score...');
      const response = await api.post('/compliance/score/recalculate');
      setScoreData(response.data);
      toast.success(`Score recalculated: ${response.data.overall_score}%`);
      // Refresh trust data to update control statuses
      fetchTrustCenter();
    } catch (error) {
      console.error('Error recalculating score:', error);
      toast.error('Failed to recalculate score');
    } finally {
      setScoreinLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating Trust Center PDF...');
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/trust/export/summary?format=pdf`, '_blank');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'configured':
        return <Badge className="bg-green-100 text-green-800">Configured</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-800">In Progress</Badge>;
      case 'needs_attention':
        return <Badge variant="destructive">Needs Attention</Badge>;
      case 'not_applicable':
        return <Badge variant="outline">N/A</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getControlIcon = (category) => {
    switch (category) {
      case 'Access Control':
        return <Users className="w-4 h-4" />;
      case 'Data Protection':
        return <Lock className="w-4 h-4" />;
      case 'Data Isolation':
        return <Database className="w-4 h-4" />;
      case 'Audit':
        return <Eye className="w-4 h-4" />;
      case 'API Security':
        return <Key className="w-4 h-4" />;
      case 'Operations':
        return <Server className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  const summary = trustData?.summary || {};
  const sections = trustData?.sections || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Shield className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Trust Center
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            Security & Compliance Controls (SOC 2 Framework)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRecalculateScore} disabled={scoringLoading}>
            {scoringLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Recalculate Score
          </Button>
          <Button variant="outline" onClick={handleExportPDF}>
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" asChild>
            <Link to="/governance/audit-trail">
              <FileText className="w-4 h-4 mr-2" />
              Audit Trail
            </Link>
          </Button>
        </div>
      </div>

      {/* Sprint 21: Auto-Calculated Compliance Score Panel */}
      {scoreData && (
        <Card className="border-[var(--clr-brand-primary)]/30 bg-gradient-to-r from-[var(--clr-brand-primary)]/5 to-transparent">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              {/* Large Score Display */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full border-8 border-[var(--clr-surface-subtle)] flex items-center justify-center">
                    <div 
                      className="absolute inset-2 rounded-full"
                      style={{
                        background: `conic-gradient(
                          ${scoreData.overall_score >= 70 ? '#22c55e' : scoreData.overall_score >= 40 ? '#f59e0b' : '#ef4444'} ${scoreData.overall_score}%, 
                          transparent ${scoreData.overall_score}%
                        )`
                      }}
                    />
                    <div className="relative z-10 bg-white rounded-full w-24 h-24 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold text-[var(--clr-text-primary)]">
                        {scoreData.overall_score}%
                      </span>
                      <span className="text-xs text-[var(--clr-text-secondary)]">Overall</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-[var(--clr-text-primary)]">
                    Compliance Score
                  </h3>
                  <p className="text-sm text-[var(--clr-text-secondary)]">
                    Auto-calculated from {scoreData.total_controls} controls
                  </p>
                  {scoreData.calculation_time_ms && (
                    <p className="text-xs text-[var(--clr-text-secondary)] mt-1">
                      Calculated in {scoreData.calculation_time_ms}ms
                      {scoreData.cached && ' (cached)'}
                    </p>
                  )}
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="flex-1 grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-600">{scoreData.configured}</p>
                  <p className="text-xs text-green-700">Configured</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-600">{scoreData.in_progress}</p>
                  <p className="text-xs text-amber-700">In Progress</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-600">{scoreData.needs_attention}</p>
                  <p className="text-xs text-red-700">Needs Attention</p>
                </div>
              </div>

              {/* Category Breakdown */}
              {scoreData.by_category?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {scoreData.by_category.map((cat) => (
                    <div 
                      key={cat.category} 
                      className="px-3 py-2 bg-white rounded-lg border shadow-sm"
                      title={`${cat.configured}/${cat.total_controls} configured`}
                    >
                      <p className="text-xs font-medium text-[var(--clr-text-secondary)]">{cat.label}</p>
                      <p className={`text-lg font-bold ${
                        cat.score_percent >= 70 ? 'text-green-600' : 
                        cat.score_percent >= 40 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {cat.score_percent}%
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">Total Controls</p>
                <p className="text-3xl font-bold text-[var(--clr-brand-primary)]">
                  {summary.total_controls || scoreData?.total_controls || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[var(--clr-brand-primary)]/10 flex items-center justify-center">
                <PieChart className="w-6 h-6 text-[var(--clr-brand-primary)]" />
              </div>
            </div>
            <Progress value={scoreData?.overall_score || summary.compliance_score || 0} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">Configured</p>
                <p className="text-3xl font-bold text-green-600">{scoreData?.configured || summary.configured || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-xs text-[var(--clr-text-secondary)] mt-2">
              Evidence verified
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">In Progress</p>
                <p className="text-3xl font-bold text-amber-600">{scoreData?.in_progress || summary.in_progress || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-xs text-[var(--clr-text-secondary)] mt-2">
              pending configuration
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">Needs Attention</p>
                <p className="text-3xl font-bold text-red-600">{scoreData?.needs_attention || summary.needs_attention || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-xs text-[var(--clr-text-secondary)] mt-2">
              Require configuration
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Trust Service Criteria Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Trust Service Criteria</CardTitle>
          <CardDescription>
            SOC 2 controls organized by trust service principle
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeSection} onValueChange={setActiveSection}>
            <TabsList className="mb-4">
              {sections.map((section) => (
                <TabsTrigger key={section.key} value={section.key} data-testid={`tab-${section.key}`}>
                  {section.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {sections.map((section) => (
              <TabsContent key={section.key} value={section.key}>
                <div className="mb-4">
                  <p className="text-[var(--clr-text-secondary)]">{section.description}</p>
                </div>

                <div className="space-y-3">
                  {section.controls?.map((control) => (
                    <div
                      key={control.id}
                      className="flex items-start gap-4 p-4 border rounded-lg hover:bg-[var(--clr-surface-subtle)] transition-colors"
                      data-testid={`control-${control.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[var(--clr-surface-subtle)] flex items-center justify-center flex-shrink-0">
                        {getControlIcon(control.category)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium text-[var(--clr-text-primary)]">
                              {control.id}: {control.name}
                            </p>
                            <p className="text-sm text-[var(--clr-text-secondary)] mt-1">
                              {control.description}
                            </p>
                          </div>
                          {getStatusBadge(control.status)}
                        </div>

                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span className="text-[var(--clr-text-secondary)]">
                            Category: {control.category}
                          </span>
                          {control.owner && (
                            <span className="text-[var(--clr-text-secondary)]">
                              Owner: {control.owner}
                            </span>
                          )}
                          {control.evidence_links?.length > 0 && (
                            <Link
                              to={control.evidence_links[0]}
                              className="text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
                            >
                              View Evidence
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* SLA Thresholds & Audit Links */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* SLA Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              SLA Thresholds
            </CardTitle>
            <CardDescription>
              Configured service level targets
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trustData?.sla_thresholds?.length > 0 ? (
              <div className="space-y-2">
                {trustData.sla_thresholds.map((sla, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] rounded-lg">
                    <span className="font-medium capitalize">{sla.stage?.replace('_', ' ')}</span>
                    <span className="text-[var(--clr-text-secondary)]">
                      {sla.value} {sla.unit}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--clr-text-secondary)]">No SLA thresholds configured</p>
            )}
            <Button variant="outline" className="w-full mt-4" asChild>
              <Link to={trustData?.sla_settings_link || '/settings/sla'}>
                <Settings className="w-4 h-4 mr-2" />
                Configure SLA Settings
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Recent Audit Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Recent Audit Activity
            </CardTitle>
            <CardDescription>
              Latest governance actions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trustData?.recent_audits?.length > 0 ? (
              <div className="space-y-2">
                {trustData.recent_audits.map((audit) => (
                  <div key={audit.id} className="flex items-center gap-3 p-3 bg-[var(--clr-surface-subtle)] rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[var(--clr-brand-primary)]/10 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-[var(--clr-brand-primary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{audit.action?.replace('_', ' ')}</p>
                      <p className="text-xs text-[var(--clr-text-secondary)]">
                        {audit.actor} • {audit.timestamp ? new Date(audit.timestamp).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--clr-text-secondary)]">No recent audit activity</p>
            )}
            <Button variant="outline" className="w-full mt-4" asChild>
              <Link to={trustData?.audit_trail_link || '/governance/audit-trail'}>
                View Full Audit Trail
                <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrustCenterPage;
